import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ThumbsUp, Star, MessageCircle } from "lucide-react";
import { api, getUser } from "../api";
import { toast } from "sonner";
import { formatProName } from "../lib/proName";
import { ShareMenu } from "./ShareMenu";
import { Linkify } from "./Linkify";

const EUR = (c, lang) => new Intl.NumberFormat(lang || "it", { style: "currency", currency: "EUR" }).format((Number.isFinite(c) ? c : 0) / 100);

function EntityImage({ src }) {
  const [broken, setBroken] = useState(false);
  if (!src || broken) return <div className="w-16 h-16 rounded-lg bg-slate-100 shrink-0" />;
  return <img src={src} alt="" className="w-16 h-16 rounded-lg object-cover shrink-0" onError={() => setBroken(true)} />;
}

// Card di anteprima dell'oggetto condiviso: dati LIVE dal backend, CTA verso la pagina pubblica reale.
function EntityCard({ e, lang, t }) {
  const to = e.type === "event" ? `/events/${e.id}`
    : e.type === "service" ? `/organizations/${e.org_id}?tab=services`
      : `/organizations/${e.org_id}?tab=shop&product=${e.id}`;
  const info = e.type === "event"
    ? [e.is_paid ? EUR(e.price_cents, lang) : t("events.free"), e.next_slot ? new Date(e.next_slot).toLocaleString(lang) : null].filter(Boolean).join(" · ")
    : e.type === "service"
      ? [e.price_cents ? EUR(e.price_cents, lang) : t("services.free"), e.duration_minutes ? `${e.duration_minutes} min` : null, [...new Set([e.location_label, e.address].filter(Boolean))].join(" · ") || null].filter(Boolean).join(" · ")
      : [EUR(e.price_cents, lang), e.has_variants ? t("shop.variants") : null].filter(Boolean).join(" · ");
  const badge = e.type === "event" ? t("nav.events") : e.type === "service" ? t("services.title") : t("shop.title");
  return (
    <Link to={to} className="mt-3 flex gap-3 items-center border border-slate-200 rounded-xl p-3 hover:border-blue-400 transition-colors" data-testid="post-entity-card">
      <EntityImage src={e.image} />
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wide text-blue-500">{badge}</div>
        <div className="font-semibold truncate" data-testid="post-entity-title">{e.title}</div>
        <div className="text-xs text-slate-500 truncate">{e.org_name}</div>
        <div className="text-xs text-slate-600 mt-0.5 truncate">{info}</div>
      </div>
      <span className="yp-btn yp-btn-outline !py-1 !px-3 text-xs shrink-0" data-testid="post-entity-open">{t("feedx.open_item")} →</span>
    </Link>);
}

const IMG_TYPES = ["image/jpeg", "image/png", "image/webp"];
const VID_TYPES = ["video/mp4", "video/webm"];
const IMG_MAX = 8 * 1024 * 1024;
const VID_MAX = 100 * 1024 * 1024;
const MAX_MEDIA = 10;

export function Avatar({ url, label, cls = "w-9 h-9 text-sm", org = false }) {
  const [broken, setBroken] = useState(false);
  const ring = org ? " ring-1 ring-slate-300" : "";
  return url && !broken
    ? <img src={url} alt={label || ""} className={`${cls} rounded-full object-cover shrink-0${ring}`}
        onError={() => setBroken(true)} data-testid="avatar-img" />
    : <div className={`${cls} rounded-full bg-slate-200 flex items-center justify-center font-bold text-slate-500 shrink-0${ring}`} data-testid="avatar-placeholder">{(label || "?")[0].toUpperCase()}</div>;
}

function VideoAttachment({ m }) {
  const { t } = useTranslation();
  const [err, setErr] = useState(false);
  if (err) return <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3" data-testid="post-video-error">{t("mediax.video_error")}</div>;
  return <video src={m.url} controls playsInline preload="metadata" onError={() => setErr(true)}
    className="w-full rounded-lg max-h-[480px] bg-black" data-testid="post-video" />;
}

function MediaGallery({ media }) {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(null);
  useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => { if (e.key === "Escape") setZoom(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom]);
  if (!media?.length) return null;
  return (
    <div className="mt-3" data-testid="post-media">
      <div className={media.length > 1 ? "grid grid-cols-2 gap-2" : ""}>
        {media.map(m => m.kind === "video" ? <VideoAttachment key={m.id} m={m} /> : (
          <button key={m.id} onClick={() => setZoom(m.url)} className="overflow-hidden rounded-lg bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label={t("mediax.zoom_image")} data-testid="post-image">
            <img src={m.url} alt="" loading="lazy" className="w-full h-auto object-contain max-h-[480px] hover:opacity-90 transition-opacity" />
          </button>))}
      </div>
      {zoom && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setZoom(null)}
          role="dialog" aria-modal="true" aria-label={t("mediax.zoom_image")} data-testid="image-lightbox">
          <img src={zoom} alt="" className="max-w-full max-h-full object-contain" />
          <button className="absolute top-4 right-4 text-white text-2xl px-3 py-1" onClick={() => setZoom(null)}
            aria-label={t("mediax.close")} data-testid="lightbox-close">✕</button>
        </div>)}
    </div>
  );
}

// Compositore condiviso professionisti/strutture: testo, immagini (max 10) o 1 video,
// anteprima con rimozione singola, upload con progress, blocco doppia pubblicazione e post vuoto.
// Anteprima del primo link http/https nel testo del post: titolo/descrizione/immagine via /api/link-preview
// (recupero metadati SSRF-safe lato server). Se non disponibile, nessuna card: il link resta cliccabile nel testo.
const URL_IN_TEXT = /https?:\/\/[^\s<>]+/i;
function LinkPreview({ body }) {
  const [pv, setPv] = useState(null);
  const url = (body || "").match(URL_IN_TEXT)?.[0]?.replace(/[)\]}.,;:!?'"»›>]+$/, "");
  useEffect(() => {
    if (!url) { setPv(null); return; }
    let on = true;
    api.get("/link-preview", { params: { url } }).then(({ data }) => { if (on) setPv(data); }).catch(() => { if (on) setPv(null); });
    return () => { on = false; };
  }, [url]);
  if (!url || !pv || (!pv.title && !pv.image && !pv.description)) return null;
  let host = ""; try { host = new URL(pv.url || url).hostname.replace(/^www\./, ""); } catch { /* host facoltativo */ }
  return (
    <a href={pv.url || url} target="_blank" rel="noopener noreferrer nofollow ugc" onClick={(e) => e.stopPropagation()}
      className="mt-3 flex gap-3 items-stretch border border-slate-200 rounded-xl overflow-hidden hover:border-blue-400 transition-colors" data-testid="post-link-preview">
      {pv.image && <img src={pv.image} alt="" loading="lazy" className="w-28 sm:w-40 object-cover shrink-0 bg-slate-100 self-stretch max-h-32" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
      <div className="min-w-0 flex-1 p-3">
        {host && <div className="text-[11px] uppercase tracking-wide text-slate-400 truncate" data-testid="link-preview-host">{host}</div>}
        {pv.title && <div className="font-semibold text-sm text-slate-800 break-words" data-testid="link-preview-title">{pv.title}</div>}
        {pv.description && <div className="text-xs text-slate-500 break-words mt-0.5">{pv.description.slice(0, 160)}</div>}
      </div>
    </a>);
}

export function PostComposer({ onPosted }) {
  const { t } = useTranslation();
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]); // {file, url, kind}
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const addFiles = (list) => {
    const arr = Array.from(list || []);
    if (!arr.length) return;
    for (const f of arr) {
      const isImg = IMG_TYPES.includes(f.type), isVid = VID_TYPES.includes(f.type);
      if (!isImg && !isVid) { toast.error(t("mediax.bad_type")); return; }
      if (isImg && f.size > IMG_MAX) { toast.error(t("mediax.image_too_large")); return; }
      if (isVid && f.size > VID_MAX) { toast.error(t("mediax.video_too_large")); return; }
    }
    const hasVideo = files.some(x => x.kind === "video") || arr.some(f => f.type.startsWith("video/"));
    if (hasVideo && files.length + arr.length > 1) { toast.error(t("mediax.only_one_video")); return; }
    if (files.length + arr.length > MAX_MEDIA) { toast.error(t("mediax.too_many")); return; }
    setFiles([...files, ...arr.map(f => ({ file: f, url: URL.createObjectURL(f), kind: f.type.startsWith("video/") ? "video" : "image" }))]);
    if (fileRef.current) fileRef.current.value = "";
  };
  const removeAt = (i) => { URL.revokeObjectURL(files[i].url); setFiles(files.filter((_, j) => j !== i)); };

  const create = async () => {
    if (busy) return; // blocco doppia pubblicazione
    if (!body.trim() && files.length === 0) { toast.error(t("mediax.empty_post")); return; }
    setBusy(true);
    const uploaded = [];
    try {
      let mediaIds = [];
      if (files.length) {
        const fd = new FormData();
        files.forEach(x => fd.append("files", x.file));
        const { data } = await api.post("/posts/media", fd, {
          onUploadProgress: (e) => setProgress(e.total ? Math.round(e.loaded / e.total * 100) : 0),
        });
        mediaIds = data.media.map(m => m.id);
        uploaded.push(...mediaIds);
      }
      await api.post("/posts", { body: body.trim(), visibility: "public", media_ids: mediaIds });
      setBody(""); files.forEach(x => URL.revokeObjectURL(x.url)); setFiles([]);
      toast.success(t("feedx.published")); onPosted?.();
    } catch (e) {
      for (const mid of uploaded) { try { await api.delete(`/posts/media/${mid}`); } catch { /* cleanup best-effort */ } }
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t("events.error_generic"));
    } finally { setBusy(false); setProgress(0); }
  };

  return (<div className="yp-card p-4 mb-6" data-testid="profile-composer">
    <textarea className="yp-input" rows={3} placeholder={t("feedx.write_something")} value={body} onChange={e=>setBody(e.target.value)} data-testid="post-body" />
    {files.length > 0 && (
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="attachment-previews">
        {files.map((x, i) => (
          <div key={i} className="relative rounded-lg overflow-hidden bg-slate-100" data-testid="attachment-preview">
            {x.kind === "video"
              ? <video src={x.url} className="w-full h-24 object-cover" muted playsInline preload="metadata" />
              : <img src={x.url} alt="" className="w-full h-24 object-cover" />}
            <button type="button" onClick={() => removeAt(i)} aria-label={t("mediax.remove")} data-testid="attachment-remove"
              className="absolute top-1 right-1 bg-black/60 text-white rounded-full w-6 h-6 text-xs leading-none">✕</button>
          </div>))}
      </div>)}
    {busy && progress > 0 && progress < 100 && (
      <div className="mt-3" data-testid="upload-progress">
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden"><div className="h-2 bg-blue-500 transition-all" style={{ width: `${progress}%` }} /></div>
        <div className="text-xs text-slate-500 mt-1">{t("mediax.uploading")} {progress}%</div>
      </div>)}
    <div className="mt-3 flex justify-between items-center gap-2 flex-wrap">
      <div>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,video/mp4,video/webm" multiple className="hidden"
          onChange={e => addFiles(e.target.files)} data-testid="post-file-input" />
        <button type="button" className="yp-btn yp-btn-outline !py-1 !px-3 text-xs" onClick={() => fileRef.current?.click()}
          disabled={busy} data-testid="post-attach-btn">📎 {t("mediax.add")}</button>
      </div>
      <button className="yp-btn yp-btn-primary" onClick={create} disabled={busy || (!body.trim() && files.length === 0)} data-testid="post-submit">
        {busy ? t("mediax.uploading") : t("feedx.publish")}
      </button>
    </div>
  </div>);
}

function Comments({ postId, count = 0, canManage = false, onChanged }) {
  const { t, i18n } = useTranslation();
  const u = getUser();
  const [open, setOpen] = useState(false);
  const [cs, setCs] = useState([]);
  const [body, setBody] = useState("");
  const load = () => api.get(`/posts/${postId}/comments`).then(({data}) => setCs(data));
  const toggle = () => { if (!open) load(); setOpen(!open); };
  const submit = async () => {
    if (!body.trim()) return;
    try { await api.post(`/posts/${postId}/comments`, { body }); setBody(""); toast.success(t("feedx.comment_pending")); load(); }
    catch(e) { toast.error(e.response?.data?.detail || t("events.error_generic")); }
  };
  const moderate = async (cid, action) => {
    try { await api.post(`/comments/${cid}/${action}`); load(); onChanged?.(null, true); }
    catch(e) { toast.error(e.response?.data?.detail || t("events.error_generic")); }
  };
  return (<div className="mt-2">
    <button className={`yp-btn !py-1 !px-3 text-xs inline-flex items-center gap-1 ${open ? "yp-btn-outline !border-blue-500 !text-blue-600 bg-blue-50" : "yp-btn-ghost"}`}
      onClick={toggle} aria-expanded={open} data-testid="comments-toggle">
      <MessageCircle size={14} aria-hidden="true" /> {t("feedx.comments")} ({count})
    </button>
    {open && (<div className="mt-2 space-y-2" data-testid="comments-list">
      {cs.map(c => (
        <div key={c.id} className={`flex gap-2 items-start text-sm rounded p-2 ${c.status === "pending" ? "bg-amber-50 border border-amber-200" : "bg-slate-50"}`} data-testid="comment-row">
          <Avatar url={c.author_avatar_url} label={c.author_nickname} cls="w-6 h-6 text-xs" />
          <div className="min-w-0 flex-1">
            <span className="font-medium">{c.author_role === "professional" ? formatProName({ title: c.author_title, first_name: c.author_first_name, last_name: c.author_last_name }, i18n.language) : (c.author_nickname || "?")}</span> <span className="text-slate-600">{c.body}</span>
            {c.status === "pending" && <span className="ml-2 bg-amber-100 text-amber-700 rounded px-1.5 py-0.5 text-[10px]" data-testid="comment-pending-badge">{t("feedx.comment_pending")}</span>}
          </div>
          {canManage && c.status === "pending" && (
            <div className="flex gap-1 shrink-0">
              <button className="yp-btn yp-btn-outline !py-0.5 !px-2 text-[11px]" onClick={() => moderate(c.id, "approve")} data-testid="comment-approve">{t("feedx.approve")}</button>
              <button className="yp-btn yp-btn-ghost !py-0.5 !px-2 text-[11px] text-red-600" onClick={() => moderate(c.id, "reject")} data-testid="comment-reject">{t("feedx.reject")}</button>
            </div>)}
        </div>))}
      {u && (<div className="flex gap-2">
        <input className="yp-input !py-1 text-sm" placeholder={t("feedx.comment_ph")} value={body} onChange={e=>setBody(e.target.value)} data-testid="comment-input" />
        <button className="yp-btn yp-btn-outline !py-1 !px-3 text-xs" onClick={submit} data-testid="comment-submit">{t("chat.send")}</button>
      </div>)}
    </div>)}
  </div>);
}

export function PostCard({ p, canManage, onChanged }) {
  const { t, i18n } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(p.body);
  const [likeBusy, setLikeBusy] = useState(false);
  const [favBusy, setFavBusy] = useState(false);
  const liked = p.my_reaction === "like";
  // Moderazione per proprietà del post, non per pagina: l'autore modera dal feed e dal profilo
  const manage = canManage || p.author_id === getUser()?.id;
  const requireLogin = () => {
    if (getUser()) return true;
    window.location.href = "/login?next=/feed";  // stesso pattern dell'interceptor per gli anonimi
    return false;
  };
  // Autore cliccabile verso la pagina pubblica reale (l'ID utente coincide con l'ID profilo in questa architettura);
  // i pazienti non hanno pagina pubblica: area non cliccabile, nessun dato privato.
  const authorTo = p.author_role === "professional" ? `/professionals/${p.author_id}`
    : p.author_role === "organization" ? `/organizations/${p.author_id}` : null;
  const toggleLike = async () => {
    if (!requireLogin()) return;  // niente ottimistico per i visitatori anonimi
    if (likeBusy) return;  // niente doppie azioni da click rapidi
    setLikeBusy(true);
    const nl = !liked;
    onChanged?.({ ...p, my_reaction: nl ? "like" : null, like_count: Math.max(0, (p.like_count || 0) + (nl ? 1 : -1)) }); // ottimistico
    try {
      if (nl) { const fd = new FormData(); fd.append("kind", "like"); await api.post(`/posts/${p.id}/react`, fd); }
      else await api.delete(`/posts/${p.id}/react`);
    } catch { onChanged?.(p); toast.error(t("events.error_generic")); }  // rollback
    finally { setLikeBusy(false); }
  };
  const toggleFav = async () => {
    if (!requireLogin()) return;
    if (favBusy) return;
    setFavBusy(true);
    const nf = !p.my_favorite;
    onChanged?.({ ...p, my_favorite: nf }); // ottimistico
    try { await api.post(`/posts/${p.id}/${nf ? "favorite" : "unfavorite"}`); }
    catch { onChanged?.(p); toast.error(t("events.error_generic")); }  // rollback
    finally { setFavBusy(false); }
  };
  const translate = async () => {
    if (!p.body) return;
    const lang = localStorage.getItem("lang") || "it";
    const { data } = await api.post("/translate", { text: p.body, target: lang });
    onChanged?.({ ...p, translated: data.translation });
  };
  const saveEdit = async () => {
    try { await api.put(`/posts/${p.id}`, { body: draft, visibility: "public" }); setEditing(false); onChanged?.(null, true); }
    catch(e) { toast.error(e.response?.data?.detail || t("events.error_generic")); }
  };
  const del = async () => {
    if (!window.confirm(t("feedx.delete_confirm"))) return;
    try { await api.delete(`/posts/${p.id}`); onChanged?.(null, true); }
    catch(e) { toast.error(e.response?.data?.detail || t("events.error_generic")); }
  };
  const authorInner = (<>
    <Avatar url={p.author_avatar_url} label={p.author_nickname} />
    <div>
      <div className="font-semibold">{p.author_role === "professional" && (p.author_first_name || p.author_last_name) ? formatProName({ title: p.author_title, first_name: p.author_first_name, last_name: p.author_last_name }, i18n.language) : (p.author_nickname || "—")}</div>
      <div className="text-xs text-slate-400">{new Date(p.created_at).toLocaleString(i18n.language)}</div>
    </div></>);
  return (<article className="yp-card p-5" data-testid="feed-post-card">
    <div className="flex justify-between items-start mb-2">
      {authorTo ? (
        <Link to={authorTo} className="flex items-center gap-2 rounded-lg -m-1 p-1 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label={p.author_nickname} data-testid="post-author-link">{authorInner}</Link>
      ) : (<div className="flex items-center gap-2 -m-1 p-1">{authorInner}</div>)}
      {p.author_role === "professional" && <span className="yp-badge yp-badge-approved">YouPAM</span>}
    </div>
    {editing ? (<div>
      <textarea className="yp-input" rows={3} value={draft} onChange={e=>setDraft(e.target.value)} data-testid="post-edit-body" />
      <div className="mt-2 flex gap-2 justify-end">
        <button className="yp-btn yp-btn-ghost !py-1 !px-3 text-xs" onClick={()=>setEditing(false)} data-testid="post-edit-cancel">{t("common.cancel")}</button>
        <button className="yp-btn yp-btn-primary !py-1 !px-3 text-xs" onClick={saveEdit} data-testid="post-edit-save">{t("common.save")}</button>
      </div>
    </div>) : (p.body ? <p className="text-slate-800 whitespace-pre-wrap break-words"><Linkify text={p.body} linkClassName="text-blue-600 underline break-words" maxLen={48} /></p> : null)}
    {!editing && p.body ? <LinkPreview body={p.body} /> : null}
    <MediaGallery media={p.media} />
    {p.entity_type && (p.entity
      ? <EntityCard e={p.entity} lang={i18n.language} t={t} />
      : <div className="mt-3 text-xs text-slate-400 border border-slate-200 rounded-lg p-3" data-testid="post-entity-unavailable">{t("feedx.item_unavailable")}</div>)}
    {p.event_id && (
      <Link to={`/events/${p.event_id}`} className="mt-3 inline-flex items-center gap-1 yp-btn yp-btn-outline !py-1 !px-3 text-xs" data-testid="post-event-link">
        {t("feedx.view_event")} →
      </Link>
    )}
    {p.translated && <p className="mt-2 pt-2 border-t border-slate-100 text-slate-600 text-sm italic">{p.translated}</p>}
    <div className="mt-3 flex gap-2 text-sm flex-wrap">
      <button className={`yp-btn !py-1 !px-3 text-xs inline-flex items-center gap-1 ${liked ? "yp-btn-outline !border-blue-500 !text-blue-600 bg-blue-50" : "yp-btn-ghost"}`}
        onClick={toggleLike} aria-pressed={liked} aria-label={t(liked ? "feedx.liked" : "feedx.like")} disabled={likeBusy} data-testid="feed-react-btn">
        <ThumbsUp size={14} aria-hidden="true" fill={liked ? "currentColor" : "none"} />
        <span data-testid="like-count">{p.like_count || 0}</span>
      </button>
      <button className={`yp-btn !py-1 !px-3 text-xs inline-flex items-center gap-1 ${p.my_favorite ? "yp-btn-outline !border-amber-500 !text-amber-600 bg-amber-50" : "yp-btn-ghost"}`}
        onClick={toggleFav} aria-pressed={!!p.my_favorite} aria-label={t(p.my_favorite ? "feedx.fav_remove" : "feedx.fav_add")} disabled={favBusy} data-testid="feed-fav-btn">
        <Star size={14} aria-hidden="true" fill={p.my_favorite ? "currentColor" : "none"} />
      </button>
      {p.body ? <button className="yp-btn yp-btn-ghost !py-1 !px-3 text-xs" onClick={translate} data-testid="feed-translate-btn">🌐 {t("common.translate")}</button> : null}
      {manage && !editing && (<>
        <button className="yp-btn yp-btn-ghost !py-1 !px-3 text-xs" onClick={()=>{ setDraft(p.body); setEditing(true); }} data-testid="post-edit-btn">{t("feedx.edit")}</button>
        <button className="yp-btn yp-btn-ghost !py-1 !px-3 text-xs text-red-600" onClick={del} data-testid="post-delete-btn">{t("feedx.delete")}</button>
      </>)}
      <ShareMenu shareUrl={`${window.location.origin}/p/post-${p.id}`} title={p.author_nickname} text={p.body ? p.body.slice(0, 120) : p.author_nickname} className="!py-1 !px-3 !text-xs" />
    </div>
    <Comments postId={p.id} count={p.comments_count || 0} canManage={manage} onChanged={onChanged} />
  </article>);
}

export function PostList({ authorId = null, canManage = false }) {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const load = async () => {
    const { data } = await api.get("/posts", { params: authorId ? { author: authorId } : {} });
    setPosts(data);
  };
  useEffect(() => { load(); }, [authorId]);
  const changed = (updated, reload) => {
    if (reload) { load(); return; }
    if (updated) setPosts(posts.map(x => x.id === updated.id ? updated : x));
  };
  return (<div className="space-y-4" data-testid="post-list">
    {posts.length === 0 && <div className="text-slate-400 text-center py-6" data-testid="post-list-empty">{t("feedx.no_posts")}</div>}
    {posts.map(p => <PostCard key={p.id} p={p} canManage={canManage} onChanged={changed} />)}
  </div>);
}

export { PostList as default };
